import useDialog from 'hooks/useDialog'
import ReportModal from 'components/Report/ReportModal'
import ConfirmModal from 'components/modals/ConfirmModal'
import { CreatePost } from '@wellesley/openapi'
import { useIsMobile } from '@wellesley/ui'
import ActionsDropdown from 'components/ActionsDropdown'
import { FC, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRelationship, userSlice } from '@wellesley/user'

import PrivacyItem from '../../PostForm/ControlsBar/PrivacyDropdown/PrivacyItem'
import { feedsSlice } from '../../feedsSlice'
import getPostReplyPrivacy from '../../PostForm/helpers/privacy/getPostReplyPrivacy'
import PostFormMobileModal from '../../PostFormButton/FeedModal'
import {
  useDeleteApiV1PostsByPostIdMutation,
  usePutApiV1PostsByPostIdMutation,
} from '../../api'

import useFeedActions from './hooks/useFeedActions'
import EditedMobileBadge from './EditedMobileBadge'
import { TFeedActionsMenu } from './types'
import useMuteUnmuteActions from './hooks/useMuteUnmuteActions'
import useBlockUnblockActions from './hooks/useBlockUnblockActions'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import UserInfoPopup from '../../../UserInfoPopup'

const FeedActionsMenu: FC<TFeedActionsMenu> = ({
  feed,
  isAuthor,
  canPinPost,
  renderFeed,
  renderEditModal,
  isVisiblePinAction,
  privacyOptionsVariant,
}) => {
  const author = feed.author
  const isGuest = !useSelector(userSlice.selectors.selectCurrentUser)

  const [isDropdownOpened, setIsDropdownOpened] = useState(false)

  const dispatch = useDispatch()
  const removePosts = (userId: string) =>
    dispatch(feedsSlice.actions.hideUserPosts(userId))

  const [isOpenEditModal, setOpenEditModal] = useState(false)

  const isMobile = useIsMobile()

  const [updateFeed, { isSuccess: isEditSuccess }] =
    usePutApiV1PostsByPostIdMutation()

  const handleUpdateFeed = async (data: CreatePost) => {
    await updateFeed({ postId: feed.id, createPost: data })
  }

  useEffect(() => {
    isEditSuccess && setOpenEditModal(false)
  }, [isEditSuccess])

  const { relationship } = useRelationship({
    userId: author.id,
    skip: !isDropdownOpened,
  })

  const [isReportOpen, setIsOpenReport] = useState(false)
  const handleCloseReport = () => setIsOpenReport(false)

  const [deleteDialog, openDeleteModal, closeDeleteModal] = useDialog()

  const [deletePost, { isLoading: isDeleting }] =
    useDeleteApiV1PostsByPostIdMutation()

  const handleDeletePost = async () => {
    await deletePost({ postId: feed.id })
    closeDeleteModal()
  }

  const [blockUnblockAction, renderBlockDialog] = useBlockUnblockActions({
    user: author,
    isAuthor,
    relationship,
  })

  const [muteUnmuteAction, renderMuteDialog] = useMuteUnmuteActions({
    user: author,
    isHidden: isAuthor || isGuest,
    relationship,
    onMuteSuccess: () => removePosts(author.id),
  })

  const feedActions = useFeedActions({
    ...{ feed, isAuthor, canPinPost, isVisiblePinAction },
    blockUnblockAction,
    muteUnmuteAction,
    onDelete: openDeleteModal,
    onEdit: () => setOpenEditModal(true),
    onReport: () => setIsOpenReport(true),
  })

  const privacy = getPostReplyPrivacy(true).find((v) => v.key === feed.privacy)

  const editModalProps = {
    feed,
    isModal: true,
    buttonText: 'Save',
    isOpenModal: isOpenEditModal,
    onSubmit: handleUpdateFeed,
    onCloseModal: () => setOpenEditModal(false),
    privacyOptionsVariant,
    children: feed?.originalPost && (
      <>
        <div className="border-1 mt-s w-full border-b border-gray-200" />
        <div className="mb-m no-scrollbar max-h-[400px] overflow-y-auto">
          {renderFeed?.({
            isActions: false,
            isFooter: false,
            isSmallImagePreview: false,
            ...feed.originalPost,
            renderUserWrapper: ({ el, user }) => (
              <UserInfoPopup username={getUserNameWithDomain(user)}>
                {el}
              </UserInfoPopup>
            ),
            children: null,
          })}
        </div>
      </>
    ),
  }

  return (
    <>
      {renderBlockDialog()}
      {renderMuteDialog?.()}
      <div
        className="flex items-center"
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
        }}
      >
        <ConfirmModal
          {...deleteDialog}
          title="Delete post"
          onClose={closeDeleteModal}
          leftButton={{
            title: 'Cancel',
            isLoading: false,
            variant: 'secondary',
            onClick: closeDeleteModal,
          }}
          rightButton={{
            title: 'Delete post',
            variant: 'red_primary',
            loaderColor: 'white',
            isLoading: isDeleting,
            onClick: () => void handleDeletePost(),
          }}
        >
          If you delete this post, it cannot be restored.
        </ConfirmModal>
        {isOpenEditModal && (
          <>
            {isMobile ? (
              <PostFormMobileModal {...editModalProps} isModal />
            ) : (
              renderEditModal?.(editModalProps)
            )}
          </>
        )}
        <ReportModal
          user={feed.author}
          isOpen={isReportOpen}
          groupId={feed.groupId}
          onClose={handleCloseReport}
          removePosts={removePosts}
          targetReportEntityId={feed.id}
        />
        <ActionsDropdown
          isPortal={false}
          actions={feedActions}
          triggerClass="size-4 border-0 p-0 rounded-[3px]"
          onOpenChange={setIsDropdownOpened}
        >
          <>
            {privacy && (
              <PrivacyItem
                {...privacy}
                className="ml-2 py-2 md:hidden md:w-0"
              />
            )}
            {feed.edited && <EditedMobileBadge date={feed.updatedAt} />}
            <div className="border-b-[1px] border-dashed border-gray-200 md:hidden" />
          </>
        </ActionsDropdown>
      </div>
    </>
  )
}

export { FeedActionsMenu }
